import { api, isApiError } from '@/helpers/apiClient'
import logger from '@/helpers/logger'
import {
  loginResponseSchema,
  passwordResetAckSchema,
  userSchema,
  type ForgotPasswordInput,
  type LoginInput,
  type LoginResponse,
  type ResetPasswordInput,
  type User,
} from '@/schemas/auth.schema'
import type { IAuthService } from '@/types/AuthService.types'

/**
 * Service for authentication endpoints. Every method validates its response
 * through the `api` client's schema boundary, so the returned values are typed
 * by the schemas, not by unchecked assertions.
 */
class AuthService implements IAuthService {
  /**
   * Log in with email and password.
   * @param input Login credentials.
   * @returns The access token and the signed-in user.
   */
  async login(input: LoginInput): Promise<LoginResponse> {
    try {
      return await api.post('/auth/login', input, loginResponseSchema)
    } catch (error) {
      logger.failure('Login failed', error)
      throw error
    }
  }

  /**
   * Fetch the currently authenticated user.
   * @returns The signed-in user.
   */
  async me(): Promise<User> {
    try {
      return await api.get('/auth/me', userSchema)
    } catch (error) {
      // 401 is an expired or revoked session; the caller signs out.
      if (isApiError(error) && error.status === 401) throw error
      logger.failure('Failed to load current user', error)
      throw error
    }
  }

  /**
   * Switch the active company context for multi-company users (or platform admins).
   * Returns a fresh token + user with permissions for that company.
   */
  async switchCompany(companyId: string): Promise<LoginResponse> {
    try {
      return await api.post(
        '/auth/switch-company',
        { companyId },
        loginResponseSchema,
      )
    } catch (error) {
      logger.failure('Failed to switch company', error, { companyId })
      throw error
    }
  }

  /**
   * Request a password-reset email. The API answers success for unknown emails
   * too, so a resolved promise only means "request accepted".
   */
  async forgotPassword(input: ForgotPasswordInput): Promise<void> {
    try {
      await api.post('/auth/forgot-password', input, passwordResetAckSchema)
    } catch (error) {
      logger.failure('Forgot password request failed', error)
      throw error
    }
  }

  /** Complete a reset with the emailed token and a new password. */
  async resetPassword(input: ResetPasswordInput): Promise<void> {
    try {
      await api.post('/auth/reset-password', input, passwordResetAckSchema)
    } catch (error) {
      logger.failure('Password reset failed', error)
      throw error
    }
  }
}

export const authService = new AuthService()